import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Table } from '@/common/components/table'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { Text } from '@/common/components/text'
import { Stack } from '@/common/components/stack'
import { Badge } from '@/common/components/badge'
import { colors } from '@/core/styles/tokens'
import { formatWorkDays } from '@/common/lib/workSchedule'
import { ReopenTimesheetDialog } from '@/common/components/reopen-timesheet-dialog'
import {
  useEmployeeDetail,
  useEmployeeNotifications,
  useEmployeeTimesheets,
  useUpdateEmployeeExemption,
  useUpdateEmployeeSchedule,
  type EmployeeDetail,
  type EmployeeTimesheetItem,
} from '@/common/hooks/useEmployeeProfile'

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso.length <= 10 ? iso + 'T00:00:00' : iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Stack gap="8">
      <Text size="lg" weight="bold">
        {title}
      </Text>
      {children}
    </Stack>
  )
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 8, fontSize: 14 }}>
      <span style={{ minWidth: 160, color: colors.gray70 }}>{label}</span>
      <span style={{ color: colors.martinique }}>{value ?? '—'}</span>
    </div>
  )
}

function IdentitySection({ emp }: { emp: EmployeeDetail }) {
  return (
    <Section title="Identity">
      <Stack gap="4">
        <Field label="Name" value={`${emp.first_name} ${emp.last_name}`} />
        <Field label="Email" value={emp.email ?? '—'} />
        <Field label="Department" value={emp.department ?? '—'} />
        <Field label="Job title" value={emp.job_title ?? '—'} />
        <Field label="Reporting to" value={emp.manager_name ?? '—'} />
        <Field label="Employment status" value={emp.employment_status} />
        <Field label="Hire date" value={fmtDate(emp.hire_date)} />
        <Field label="Employee ID" value={emp.id} />
        <Field label="BambooHR ID" value={emp.bamboo_employee_id ?? '—'} />
        <Field label="FusionAuth user ID" value={emp.auth_user_id ?? '—'} />
      </Stack>
    </Section>
  )
}

const WEEKDAYS = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
]

const linkBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: colors.sea,
  cursor: 'pointer',
  fontSize: 13,
  alignSelf: 'flex-start',
}

function ScheduleSection({ emp }: { emp: EmployeeDetail }) {
  const update = useUpdateEmployeeSchedule(emp.id)
  const [editing, setEditing] = useState(false)
  const [days, setDays] = useState<number[]>(emp.work_days)
  const [hours, setHours] = useState<string>(String(emp.standard_daily_hours))

  const startEdit = () => {
    setDays(emp.work_days)
    setHours(String(emp.standard_daily_hours))
    setEditing(true)
  }
  const toggleDay = (d: number) =>
    setDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]))
  const save = () =>
    update.mutate(
      { work_days: days, standard_daily_hours: Number(hours) },
      { onSuccess: () => setEditing(false) },
    )

  if (!editing) {
    return (
      <Section title="Work schedule">
        <Stack gap="4">
          <Field label="Working days" value={formatWorkDays(emp.work_days)} />
          <Field label="Hours per day" value={`${emp.standard_daily_hours}h`} />
          <button onClick={startEdit} style={linkBtn}>
            Edit schedule
          </button>
        </Stack>
      </Section>
    )
  }

  return (
    <Section title="Work schedule">
      <Stack gap="8">
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {WEEKDAYS.map((w) => (
            <label key={w.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
              <input type="checkbox" checked={days.includes(w.value)} onChange={() => toggleDay(w.value)} />
              {w.label}
            </label>
          ))}
        </div>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
          Hours per working day
          <input
            type="number"
            min={0.5}
            max={24}
            step={0.5}
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            style={{ width: 80, padding: '4px 8px', border: `1px solid ${colors.gray20}`, borderRadius: 6 }}
          />
        </label>
        {update.isError && <Text color="error">Failed to save: {(update.error as Error).message}</Text>}
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={save}
            disabled={update.isPending || days.length === 0}
            style={{
              padding: '6px 14px',
              borderRadius: 6,
              border: `1px solid ${colors.sea}`,
              background: colors.sea,
              color: colors.white,
              cursor: update.isPending || days.length === 0 ? 'default' : 'pointer',
              fontSize: 13,
              opacity: update.isPending || days.length === 0 ? 0.6 : 1,
            }}
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
          <button onClick={() => setEditing(false)} style={linkBtn}>
            Cancel
          </button>
        </div>
      </Stack>
    </Section>
  )
}

function ExemptionSection({ emp }: { emp: EmployeeDetail }) {
  const update = useUpdateEmployeeExemption(emp.id)
  const toggle = () => update.mutate({ timesheet_exempt: !emp.timesheet_exempt })

  return (
    <Section title="Timesheet exemption">
      <Stack gap="4">
        <Field
          label="Submits timesheets"
          value={emp.timesheet_exempt ? 'No — exempt' : 'Yes'}
        />
        <Text size="sm" color="muted">
          Exempt employees keep their timesheet history, but receive no reminders, are hidden from
          their manager's team and approval views, and can no longer create or submit timesheets.
        </Text>
        {update.isError && <Text color="error">Failed to save: {(update.error as Error).message}</Text>}
        <button onClick={toggle} disabled={update.isPending} style={linkBtn}>
          {update.isPending
            ? 'Saving…'
            : emp.timesheet_exempt
              ? 'Require timesheets again'
              : 'Mark exempt from timesheets'}
        </button>
      </Stack>
    </Section>
  )
}

/** Only filed timesheets can be sent back to the employee for correction. */
function isReopenable(ts: EmployeeTimesheetItem): boolean {
  return ts.status === 'submitted' || ts.status === 'approved'
}

function TimesheetsSection({ employeeId }: { employeeId: number }) {
  const { data, isLoading, error } = useEmployeeTimesheets(employeeId)
  const [reopenTarget, setReopenTarget] = useState<EmployeeTimesheetItem | null>(null)
  return (
    <Section title="Timesheets">
      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <Text color="error">Failed to load timesheets: {error.message}</Text>
      ) : !data || data.length === 0 ? (
        <Text color="muted">No timesheets for this employee.</Text>
      ) : (
        <Table.Root size="md">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Pay period</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell align="right">Total</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell align="right">Std</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell align="right">OT</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Submitted</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Approved</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {data.map((ts) => (
              <Table.Row key={ts.timesheet_id}>
                <Table.Cell>
                  {fmtDate(ts.pay_period_start)} – {fmtDate(ts.pay_period_end)}
                </Table.Cell>
                <Table.Cell>{ts.status}</Table.Cell>
                <Table.Cell align="right">{ts.total_hours.toFixed(2)}</Table.Cell>
                <Table.Cell align="right">{ts.standard_hours.toFixed(2)}</Table.Cell>
                <Table.Cell align="right">
                  {(ts.straight_overtime_hours + ts.flsa_overtime_hours).toFixed(2)}
                </Table.Cell>
                <Table.Cell>{fmtDateTime(ts.submitted_at)}</Table.Cell>
                <Table.Cell>{fmtDateTime(ts.approved_at)}</Table.Cell>
                <Table.Cell>
                  <span style={{ display: 'inline-flex', gap: 12, alignItems: 'center' }}>
                    <Link
                      to="/manager/review/$timesheetId"
                      params={{ timesheetId: String(ts.timesheet_id) }}
                      style={{ color: colors.sea, fontSize: 13 }}
                    >
                      View
                    </Link>
                    {isReopenable(ts) && (
                      <button
                        onClick={() => setReopenTarget(ts)}
                        style={{ ...linkBtn, color: colors.error }}
                        title="Send this timesheet back to the employee for correction"
                      >
                        Reopen
                      </button>
                    )}
                  </span>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}
      <ReopenTimesheetDialog
        timesheetId={reopenTarget?.timesheet_id ?? null}
        payPeriodLabel={
          reopenTarget
            ? `${fmtDate(reopenTarget.pay_period_start)} – ${fmtDate(reopenTarget.pay_period_end)}`
            : undefined
        }
        onClose={() => setReopenTarget(null)}
      />
    </Section>
  )
}

function NotificationsSection({ employeeId }: { employeeId: number }) {
  const { data, isLoading, error } = useEmployeeNotifications(employeeId)
  if (isLoading) return <Section title="Notifications"><LoadingSpinner /></Section>
  if (error)
    return (
      <Section title="Notifications">
        <Text color="error">Failed to load notifications: {error.message}</Text>
      </Section>
    )
  const sent = data?.sent ?? []
  const inApp = data?.in_app ?? []
  return (
    <Section title="Notifications">
      <Text size="sm" weight="bold" color="muted">
        Sent ({sent.length})
      </Text>
      {sent.length === 0 ? (
        <Text color="muted">Nothing sent to this employee.</Text>
      ) : (
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Type</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Channel</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Sent</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sent.map((n) => (
              <Table.Row key={n.id}>
                <Table.Cell>{n.event_type}</Table.Cell>
                <Table.Cell>{n.channel}</Table.Cell>
                <Table.Cell>
                  {n.status === 'failed' ? <Text color="error">failed</Text> : n.status}
                </Table.Cell>
                <Table.Cell>{fmtDateTime(n.sent_at)}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}

      <Text size="sm" weight="bold" color="muted">
        In-app ({inApp.length})
      </Text>
      {inApp.length === 0 ? (
        <Text color="muted">No in-app notifications.</Text>
      ) : (
        <Stack gap="4">
          {inApp.map((n) => (
            <Text key={n.id} size="sm">
              <strong>{n.title}</strong> — {n.message} · {fmtDateTime(n.created_at)}
              {n.read ? '' : ' · unread'}
            </Text>
          ))}
        </Stack>
      )}
    </Section>
  )
}

function FusionAuthSection({ emp }: { emp: EmployeeDetail }) {
  const fa = emp.fusionauth
  return (
    <Section title="FusionAuth profile">
      {!fa.available ? (
        <Text color="muted">FusionAuth profile unavailable{fa.reason ? `: ${fa.reason}` : ''}.</Text>
      ) : (
        <Stack gap="4">
          <Field label="Roles" value={fa.roles.length ? fa.roles.join(', ') : '—'} />
          <Field label="FusionAuth ID" value={fa.user_id ?? '—'} />
          <Field label="Email" value={fa.email ?? '—'} />
          <Field label="Active" value={fa.active == null ? '—' : fa.active ? 'yes' : 'no'} />
          <Field label="Email verified" value={fa.verified == null ? '—' : fa.verified ? 'yes' : 'no'} />
          <Field label="Last login" value={fmtDateTime(fa.last_login_at)} />
          {fa.raw && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: 'pointer', color: colors.sea, fontSize: 13 }}>
                Raw FusionAuth JSON
              </summary>
              <pre
                style={{
                  background: colors.gray5,
                  border: `1px solid ${colors.gray20}`,
                  borderRadius: 6,
                  padding: 12,
                  fontSize: 12,
                  overflow: 'auto',
                  maxHeight: 400,
                }}
              >
                {JSON.stringify(fa.raw, null, 2)}
              </pre>
            </details>
          )}
        </Stack>
      )}
    </Section>
  )
}

function EmployeeProfile() {
  const { employeeId } = Route.useParams()
  const id = Number(employeeId)
  const { data: emp, isLoading, error } = useEmployeeDetail(id)

  if (isLoading) return <div style={{ padding: '24px 32px' }}><LoadingSpinner /></div>
  if (error)
    return (
      <div style={{ padding: '24px 32px' }}>
        <Text color="error">Failed to load profile: {error.message}</Text>
      </div>
    )
  if (!emp) return null

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1100 }}>
      <Stack gap="24">
        <Stack gap="4">
          <Link to="/org/users" style={{ color: colors.sea, fontSize: 13 }}>
            ← Back to Users
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text size="xl" weight="bold">
              {emp.first_name} {emp.last_name}
            </Text>
            {emp.on_leave && (
              <Badge color="caution" size="md">
                On Leave
              </Badge>
            )}
            {emp.timesheet_exempt && (
              <Badge color="neutral" size="md">
                Timesheet exempt
              </Badge>
            )}
          </div>
          <Text color="muted">{emp.email ?? '—'}</Text>
        </Stack>

        <IdentitySection emp={emp} />
        <ScheduleSection emp={emp} />
        <ExemptionSection emp={emp} />
        <TimesheetsSection employeeId={id} />
        <NotificationsSection employeeId={id} />
        <FusionAuthSection emp={emp} />
      </Stack>
    </div>
  )
}

export const Route = createFileRoute('/org/users/$employeeId')({
  component: EmployeeProfile,
})
